import React from "react";
import { isEqual } from "lodash";
import TrimbleMaps from "@trimblemaps/trimblemaps-js";
import { withMapContext } from "./MapContext";

// FrameRoute function is called only when route is created, We can replace it with dummy function to remove its effect
// It also an instance member of Route object, we don't need it in our app yet
TrimbleMaps.Route.prototype.frameRoute = function (event) {};

// Prevent error thrown in console when no image is found
const removeImage = TrimbleMaps.Map.prototype.removeImage;
TrimbleMaps.Map.prototype.removeImage = function (imageName) {
  if (this.hasImage(imageName)) {
    return removeImage.call(this, imageName);
  }
  return "Image not found " + imageName;
};

// Prevent error while rendering route on switching to satellite themes
const initMap = TrimbleMaps.Route.prototype._initMap;
TrimbleMaps.Route.prototype._initMap = function (map) {
  const mapObj = map ?? { hasImage: () => true, getLayer: () => true };
  return initMap.call(this, mapObj);
};

// Remove keys with falsy values, except keep numeric 0
const omitNullishFields = (object) =>
  Object.fromEntries(
    Object.entries(object).filter(([, value]) => Boolean(value) || value === 0)
  );

class Directions extends React.Component {
  route = null;
  popup = new TrimbleMaps.Popup({
    closeButton: false,
    closeOnClick: false,
    className: "popupStyle",
  });
  driverLocationUpdated = false;
  pendingStyleUpdate = false;

  componentDidMount() {
    if (this.props.isStyleLoaded) {
      this.renderRoute();
    }
    this.props.map.on("style.load", this.onStyleLoad);
    this.props.map.on("idle", this.onMapIdle);
  }

  onStyleLoad = () => {
    this.pendingStyleUpdate = true;
    //update in case layer already exists
    if (this.route && this.props.map.getLayer(this.route._routeLayerName)) {
      this.updateRouteLineStyle();
      this.pendingStyleUpdate = false;
    }
  };

  onMapIdle = () => {
    // When map is idle and we have a pending style update
    if (this.pendingStyleUpdate && this.route && this.props.map.getLayer(this.route._routeLayerName)) {
      this.updateRouteLineStyle();
      this.pendingStyleUpdate = false;
    }
  };

  componentDidUpdate(prevProps) {
    //TODO: check if we can remove refreshOnMapStyleLoaded prop.
    // Map is not firing on style loaded when drawing tools are not used inside map hence it doesn't display directions on such map
    if(this.isTruckPropertiesChanged(this.props, prevProps)){
      this.destroyRoute();
    }
    if (
      (this.props.isStyleLoaded ||
        (this.props.refreshOnMapStyleLoaded &&
          this.props.map.isStyleLoaded())) &&
      !this.route
    ) {
      this.renderRoute();
    }

    if (
      this.route &&
      this.props.map.getLayer(this.route._routeLayerName) &&
      this.props.map?.getPaintProperty(
        this.route._routeLayerName,
        "line-opacity"
      ) !== 1
    ) {
      this.updateRouteLineStyle();
    }

    this.driverLocationUpdated = !isEqual(
      this.props.driverLocation,
      prevProps.driverLocation
    );

    if (
      this.route &&
      (!isEqual(this.props.waypoints, prevProps.waypoints) ||
        !isEqual(this.props.origin, prevProps.origin) ||
        !isEqual(this.props.driverLocation, prevProps.driverLocation) ||
        !isEqual(this.props.destination, prevProps.destination))
    ) {
      this.route.update({
        stops: this.getStops(),
      });
    }

    if (this.route && this.props.data?.color !== prevProps.data.color) {
      this.route.update({
        routeColor: this.props.data.color,
      });
    }
  }

  getStops = () => {
    return [
      this.props.origin
        ? new TrimbleMaps.LngLat(this.props.origin.lng, this.props.origin.lat)
        : null,
      this.props.driverLocation
        ? new TrimbleMaps.LngLat(
            this.props.driverLocation.lng,
            this.props.driverLocation.lat
          )
        : null,
      ...this.props.waypoints.map(
        (waypoint) =>
          new TrimbleMaps.LngLat(waypoint.location.lng, waypoint.location.lat)
      ),
    ].filter((location) => location?.lat && location?.lng);
  };

  isTruckPropertiesChanged = (currProps, prevProps) => {
    return (
      currProps.truckProperties?.length !== prevProps.truckProperties?.length ||
      currProps.truckProperties?.weight !== prevProps.truckProperties?.weight
    );
  };

  renderRoute = () => {
    const truckParams = omitNullishFields({
      trkLength: this.props.truckProperties?.length,
      trkWeight: this.props.truckProperties?.weight,
    });
    this.route = new TrimbleMaps.Route({
      routeId: this.props.data.routeId,
      stops: this.getStops(),
      routeColor: this.props.data.color,
      showStops: false,
      routeType : TrimbleMaps.Common.RouteType.PRACTICAL,
      vehicleType: TrimbleMaps.Common.VehicleType.TRUCK,
      //hazMatType: TrimbleMaps.Common.HazMatType.GENERAL,
      trkUnits: TrimbleMaps.Common.TruckUnit.ENGLISH,
      ...truckParams,
      bordersOpen: false,
      ferryDiscourage: true,
      tollRoads: TrimbleMaps.Common.TollRoadsType.AVOID_IF_POSSIBLE,
      frameRoute: false
      //trkHeight: MapConstant.truckProfile.costing_options.height,
      //trkLength: MapConstant.truckProfile.costing_options.length,
      //trkWidth: MapConstant.truckProfile.costing_options.width,
      //trkWeight: MapConstant.truckProfile.costing_options.weight,
    });

    const routeLayerName = this.route._routeLayerName;

    this.route.addTo(this.props.map);
    this.hideStopsLayer();
    this.updateRouteLineStyle();

    this.props.map.on("click", routeLayerName, this.onRouteClicked);
    this.props.map.on("mouseenter", routeLayerName, this.showPopup);
    this.props.map.on("mouseleave", routeLayerName, this.hidePopup);
    this.props.map.on("sourcedata", this.reanimate);
  };

  updateRouteLineStyle = () => {
    const routeLayerName = this.route._routeLayerName;
    this.props.map.setPaintProperty(routeLayerName, "line-opacity", 1);

    switch (this.props.pathType ) {
      case "line": {
        this.props.map.setPaintProperty(routeLayerName, "line-width", 4);
        break;
      }
      case "dotted": {
        this.props.map.setPaintProperty(routeLayerName, "line-width", 4);
        this.props.map.setPaintProperty(
          routeLayerName,
          "line-dasharray",
          [1, 2]
        );
        break;
      }
      default:{
        this.props.map.setPaintProperty(routeLayerName, "line-width", 4);
        break;
      }
    }
  }

  reanimate = (event) => {
    if (
      event.sourceId === this.route._routeSourceName &&
      this.props.routeAnimation &&
      event.isSourceLoaded &&
      !(
        event.target.isMoving() ||
        event.target.isZooming() ||
        event.target.isRotating()
      ) &&
      !this.driverLocationUpdated
    ) {
      const sources = this.props.map.getStyle().sources;
      const coordinates = [];
      Object.keys(sources)
        .filter((key) => key.includes("stop-source"))
        .map((key) => {
          sources[key].data.features.forEach((feature) => {
            //Read all stop Locations
            coordinates.push(feature.geometry.coordinates);
          });
        });

      if (coordinates.length) {
        let bounds = coordinates.reduce(function (bounds, coord) {
          return bounds.extend(coord);
        }, new TrimbleMaps.LngLatBounds().extend(coordinates[0])); //new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));

        this.props.map.fitBounds(bounds, {
          padding: 50,
        });
        this.props.onAnimationDone?.();
      }
    }
  };

  componentWillUnmount() {
    this.popup?.remove();
    this.props.map.off("style.load", this.onStyleLoad);
    this.props.map.off("idle", this.onMapIdle);
    if (this.props.map._removed) {
      return;
    }
    this.destroyRoute();
  }

  destroyRoute = () => {
    if (this.route?._routeLayerName) {
      if (this.props.map.getLayer(this.route._routeLayerName)) {
        this.hideStopsLayer();
        this.route?.remove();
      }
      const { _routeLayerName } = this.route;
      this.props.map.off("click", _routeLayerName, this.onRouteClicked);
      this.props.map.off("mouseenter", _routeLayerName, this.showPopup);
      this.props.map.off("mouseleave", _routeLayerName, this.hidePopup);
      this.props.map.off("sourcedata", this.reanimate);
      this.route = null;
    }
  }

  hideStopsLayer = () => {
    this.route._defaultOriginIcon = "";
    this.route._defaultDestinationIcon = "";
    this.route._defaultStopIcon = "";
    if (this.props.map.getLayer(this.route._stopLayerName)) {
      this.props.map.removeLayer(this.route._stopLayerName);
    }
  };

  showPopup = (event) => {
    if (this.props.enableRouteHover) {
      this.props.map.getCanvas().style.cursor = "pointer";
      if (event.lngLat && this.props.hoverContent) {
        this.popup
          .setLngLat([event.lngLat.lng, event.lngLat.lat])
          .setHTML(this.props.hoverContent)
          .addTo(this.props.map);
      }
    }
  };
  hidePopup = (event) => {
    this.props.map.getCanvas().style.cursor = "grab";
    this.popup.remove();
  };

  onRouteClicked = (event) => {
    if (this.props.onClick) {
      const eventParams = {
        position: event.lngLat,
        data: this.props.data,
      };
      this.props.onClick(eventParams);
    }
  };

  render() {
    return null;
  }
}

export default withMapContext(Directions);
